import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { RheaLoader } from "@/components/RheaLoader";
import { RheaLoadingContext, MIN_VISIBLE_MS, SAFETY_MS } from "./RheaLoadingContext";

export function RheaLoadingProvider({ children }: { children: ReactNode }) {
  const [visible, setVisible] = useState(false);
  const [message, setMessage] = useState("Buscando experiencias reales...");
  const tokenRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hide = useCallback((token: number) => {
    if (tokenRef.current === token) setVisible(false);
  }, []);

  const startLoading = useCallback((msg?: string): (() => void) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    tokenRef.current += 1;
    const token     = tokenRef.current;
    const startedAt = Date.now();                    // per-operation, captured in closure
    setMessage(msg ?? "Buscando experiencias reales...");     // always reset (clears previous custom msg)
    setVisible(true);
    timerRef.current = setTimeout(() => hide(token), SAFETY_MS);

    return function finish() {
      if (tokenRef.current !== token) return;        // stale finish → no-op, timer untouched
      if (timerRef.current) clearTimeout(timerRef.current);
      const elapsed = Date.now() - startedAt;        // uses closure value, not shared ref
      const delay   = Math.max(0, MIN_VISIBLE_MS - elapsed);
      timerRef.current = setTimeout(() => hide(token), delay);
    };
  }, [hide]);

  const clearLoading = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    tokenRef.current += 1;   // invalidate any in-flight finish() or safety timer
    setVisible(false);
  }, []);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  return (
    <RheaLoadingContext.Provider value={{ startLoading, clearLoading, isOverlayVisible: visible }}>
      {children}
      <RheaLoader visible={visible} message={message} />
    </RheaLoadingContext.Provider>
  );
}
